import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ClientsService} from "@core/services/backend/common/service/clients.service";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {Client} from "@core/services/backend/common/interface/clients";
import {ClientsCreateUpdateComponent} from "@app/pages/administration/clients-create-update/clients-create-update.component";

@Component({
    selector: 'vex-clients',
    templateUrl: './clients.component.html',
    styleUrls: ['./clients.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class ClientsComponent implements OnInit {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    clientsList: Client[] = [];
    dataSource: MatTableDataSource<Client> | null;
    searchForm: FormGroup;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<Client>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.NOMBRE', property: 'name', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'GRIDS.ACTIVO', property: 'active', type: 'image', visible: true},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    isLoading: boolean = false;

    constructor(
        private clients: ClientsService,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            name: new FormControl()
        });
        this.loadData();
    }

    loadData() {
        const that = this;
        this.isLoading = true;
        this.initializeData().then(() => {
            this.dataSource.data = that.clientsList;
            this.isLoading = false;
        });
    }

    private getSearch() {
        return {
            name: this.searchForm.get("name")?.value ? '%' + this.searchForm.get("name")?.value.toString().toUpperCase() + '%' : ''
        };
    }

    async initializeData() {
        const that = this;
        return await new Promise<void>((resolve, reject) => {
            const list = this.clients.list(JSON.stringify(that.getSearch()), that.currentPage, that.pageSize, 'name ASC');
            list.pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                    that.clientsList = data.data || data || [];
                    that.paginator.pageIndex = that.currentPage;
                    that.paginator.length = data.total_records || data.totalRecords || that.clientsList.length;
                    resolve();
                },
                error: (err) => {
                    console.error('Error loading clients:', err);
                    that.clientsList = [];
                    resolve();
                }
            });
        });
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadData();
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        return this.columns.filter(column => column.visible).map(column => column.property);
    }

    buscar() {
        this.currentPage = 0;
        this.loadData();
    }

    createClient() {
        this.dialog.open(ClientsCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    updateClient(client: Client) {
        this.dialog.open(ClientsCreateUpdateComponent, {
            data: client,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    deleteClient(client: Client) {
        const that = this;
        this.clients.delete(client).subscribe({
            next(result) {
                that.snackbar.open('Cliente eliminado correctamente', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al eliminar el cliente', '', {
                    duration: 3000
                });
            }
        });
    }
}

